/* eslint-disable max-len */
// src/components/common/PostSummaryModal.js

import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, deleteDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { functions, db } from '../../firebaseConfig';
import { useToast } from '../shared/contexts/ToastContext';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import Modal from './Modal';
import {
    FiEdit2, FiSend, FiImage, FiRefreshCw, FiTrash2, FiLoader,
    FiCalendar, FiCheckCircle, FiAlertCircle, FiClock, FiExternalLink
} from 'react-icons/fi';
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import EditPostModal from './EditPostModal';

const deployFn = httpsCallable(functions, 'postContentNow', { timeout: 300000 });
const generateVisualFn = httpsCallable(functions, 'generateImageFromPrompt');

// --- Helper: Format Time ---
const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Not scheduled';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

function PostSummaryModal({ isOpen, onClose, post, project, onUpdate }) {
    const { addToast } = useToast();
    const { confirm } = useConfirmDialog();
    const [loading, setLoading] = useState(false);
    const [actionType, setActionType] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    // --- Determine Post Status ---
    const isReady = post?.status === 'scheduled' &&
                    (post?.visualStatus === 'ready' || post?.visualStatus === 'complete' || post?.visual?.facebook?.url || post?.visual?.instagram?.url);
    const isFailed = post?.status === 'failed';
    const isMissingVisual = post?.visualStatus === 'queued' || post?.visualStatus === 'pending' || 
                           (!post?.visual?.facebook?.url && !post?.visual?.instagram?.url);
    const isScheduled = post?.status === 'scheduled';
    const isPublished = post?.status === 'posted';

    // --- Platform Info ---
    const hasFacebook = post?.platforms?.facebook !== false;
    const hasInstagram = post?.platforms?.instagram !== false;


    // --- Platform Toggle State ---
    // Default to a connected platform, prefer 'facebook'
    const [viewPlatform, setViewPlatform] = useState(() => {
        if (post?.platforms?.facebook !== false) return 'facebook';
        if (post?.platforms?.instagram !== false) return 'instagram';
        return 'facebook';
    });
    
    // Ensure we don't get stuck on a disabled platform if post updates
    useEffect(() => {
        if (post) {
            if (viewPlatform === 'facebook' && post.platforms?.facebook === false) setViewPlatform('instagram');
        }
    }, [post, viewPlatform]);


    // --- Content Preview (Dynamic) ---
    const content = post?.content?.[viewPlatform] || post?.content || post?.prompt || '';
    const hashtags = post?.hashtags?.[viewPlatform] || post?.hashtags || '';
    
    // Visual: Try platform specific, then generic url
    const visualUrl = post?.visual?.[viewPlatform]?.url || post?.visual?.url || null;
    const visualObj = post?.visual?.[viewPlatform] || post?.visual || null;
    const isVideo = visualObj?.type === 'video' || visualObj?.metadata?.contentType === 'video/mp4';

    // --- Status Badge ---
    const getStatusBadge = () => {
        if (!post) return { label: 'Unknown', color: 'bg-white/10 text-white/50', icon: <FiClock /> };
        if (isPublished) return { label: 'Published', color: 'bg-pastel-green-bg/30 text-pastel-green border border-pastel-green/30', icon: <FiCheckCircle /> };
        if (isFailed) return { label: 'Failed', color: 'bg-red-500/10 text-red-400 border border-red-500/20', icon: <FiAlertCircle /> };
        if (isMissingVisual) return { label: 'Missing Visual', color: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20', icon: <FiImage /> };
        if (isReady) return { label: 'Ready to Publish', color: 'bg-slate-blue/10 text-slate-blue border border-slate-blue/20', icon: <FiCheckCircle /> };
        return { label: 'Scheduled', color: 'bg-blue-500/10 text-blue-400 border border-blue-500/20', icon: <FiClock /> };
    };
    const statusBadge = getStatusBadge();

    // --- Action Handlers ---
    const handleDeployNow = useCallback(async () => {
        if (!post || !project) return;
        const confirmed = await confirm({ title: 'Deploy Now', message: `Deploy this post to ${viewPlatform === 'both' ? 'both platforms' : viewPlatform} immediately?`, confirmLabel: 'Deploy', variant: 'danger' });
        if (!confirmed) return;

        setLoading(true);
        setActionType('deploy');
        
        try {
            const result = await deployFn({
                projectId: project.id,
                postId: post.id,
                platform: viewPlatform // Use selected platform
            });

            if (result.data?.success) {
                addToast('Post deployed successfully!', 'success');
                if (onUpdate) onUpdate();
                onClose();
            } else {
                throw new Error(result.data?.error || 'Deploy failed');
            }
        } catch (err) {
            addToast("Deploy failed. Please try again.", 'error');
        } finally {
            setLoading(false);
            setActionType(null);
        }
    }, [project, post, confirm, addToast, onUpdate, onClose, viewPlatform]);

    const handleGenerateVisual = useCallback(async () => {
        if (!post || !project) return;
        setLoading(true);
        setActionType('generate');

        try {
            const result = await generateVisualFn({
                projectId: project.id,
                postId: post.id,
                prompt: post.visualPrompt || content,
                platform: viewPlatform // Generate for specific platform if needed, or backend handles 'both' if tailored
            });

            if (result.data?.success) {
                addToast('Visual generation started!', 'success');
                if (onUpdate) onUpdate();
            } else {
                throw new Error(result.data?.error || 'Generation failed');
            }
        } catch (err) {
            addToast("Generation failed. Please try again.", 'error');
        } finally {
            setLoading(false);
            setActionType(null);
        }
    }, [project, post, content, addToast, onUpdate, viewPlatform]);

    const handleRetry = useCallback(async () => {
        if (!post || !project) return;
        const confirmed = await confirm({ title: 'Publish Now', message: 'Retry publishing this post immediately?', confirmLabel: 'Publish', variant: 'danger' });
        if (!confirmed) return;

        setLoading(true);
        setActionType('retry');

        try {
            // Reset failure fields first
            await updateDoc(doc(db, 'projects', project.id, 'posts', post.id), {
                status: 'scheduled',
                attempts: 0,
                lastAttemptError: null,
                lastAttemptAt: null,
                expireAt: deleteField(),
                updatedAt: serverTimestamp()
            });

            // Publish immediately via postContentNow (has smart per-platform skip logic)
            const result = await deployFn({
                projectId: project.id,
                postId: post.id,
                platform: viewPlatform
            });

            if (result.data?.success) {
                addToast('Post published successfully!', 'success');
                if (onUpdate) onUpdate();
                onClose();
            } else {
                throw new Error(result.data?.error || 'Publish failed');
            }
        } catch (err) {
            addToast("Publish failed. Please try again.", 'error');
        } finally {
            setLoading(false);
            setActionType(null);
        }
    }, [project, post, confirm, addToast, onUpdate, onClose, viewPlatform]);

    const handleCancel = useCallback(async () => {
        if (!post || !project) return;
        const confirmed = await confirm({ title: 'Cancel Post', message: 'Cancel this scheduled post?', confirmLabel: 'Cancel Post', variant: 'danger' });
        if (!confirmed) return;

        setLoading(true);
        setActionType('cancel');

        try {
            await deleteDoc(doc(db, 'projects', project.id, 'posts', post.id));
            addToast('Post cancelled.', 'success');
            if (onUpdate) onUpdate();
            onClose();
        } catch (err) {
            addToast("Cancel failed. Please try again.", 'error');
        } finally {
            setLoading(false);
            setActionType(null);
        }
    }, [project, post, confirm, addToast, onUpdate, onClose]);

    const handleEdit = useCallback(() => {
        setShowEditModal(true);
    }, []);

    if (!isOpen || !post || !project) return null;

    if (showEditModal) {
        return (
            <EditPostModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    if (onUpdate) onUpdate();
                }}
                post={post}
                project={project}
                initialPlatform={viewPlatform} // Pass selected platform
            />
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Post Details">
            <div className="space-y-6">
                
                {/* Date & Platforms */}
                <div className="flex flex-wrap gap-4 text-sm border-b border-white/5 pb-4">
                    <div className="flex items-center gap-2 text-white/70">
                        {isPublished ? <FiCheckCircle className="h-4 w-4 text-pastel-green" /> : <FiCalendar className="h-4 w-4 text-slate-blue" />}
                        <span className="font-medium">
                            {isPublished
                                ? `Published ${formatDateTime(post.postedAt || post.scheduledTime)}`
                                : formatDateTime(post.scheduledTime || post.createdAt)
                            }
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-white/70">
                        <div className="flex bg-white/5 rounded-full p-1 border border-white/5">
                            {hasFacebook && (
                                <button
                                    onClick={() => setViewPlatform('facebook')}
                                    className={`p-2 rounded-full transition-all ${viewPlatform === 'facebook' ? 'bg-blue-500 text-white shadow-glow' : 'text-cream/30 hover:text-white'}`}
                                    title="View Facebook Content"
                                >
                                    <FaFacebook size={14} />
                                </button>
                            )}
                            {hasInstagram && (
                                <button
                                    onClick={() => setViewPlatform('instagram')}
                                    className={`p-2 rounded-full transition-all ${viewPlatform === 'instagram' ? 'bg-pink-500 text-white shadow-glow' : 'text-cream/30 hover:text-white'}`}
                                    title="View Instagram Content"
                                >
                                    <FaInstagram size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content Preview */}
                <div className="flex gap-4">
                    <div className="w-32 h-32 flex-shrink-0 bg-warm-dark rounded-lg overflow-hidden border border-white/10">
                        {visualUrl ? (
                            isVideo ? (
                                <video className="w-full h-full object-cover" controls>
                                    <source src={visualUrl} type={visualObj?.metadata?.contentType || 'video/mp4'} />
                                </video>
                            ) : (
                                <img src={visualUrl} alt="Post visual" className="w-full h-full object-cover" />
                            )
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/20">
                                <FiImage className="h-8 w-8" />
                            </div>
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <p className="text-sm text-white/80 leading-relaxed line-clamp-4">
                            {typeof content === 'string' && content ? content : <span className="italic opacity-50">No content generated for {viewPlatform}</span>}
                        </p>
                        {hashtags && (
                            <p className="text-xs text-slate-blue mt-2 line-clamp-2">
                                {hashtags}
                            </p>
                        )}
                    </div>
                </div>

                {/* Status */}
                <div className="flex items-center gap-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/50">Status:</span>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusBadge.color}`}>
                        {statusBadge.icon}
                        {statusBadge.label}
                    </span>
                </div>

                {/* Error Message */}
                {isFailed && post.lastAttemptError && (
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-xs text-red-300 font-bold uppercase tracking-wider">Error Report:</p>
                        <p className="text-xs text-red-200 mt-1">{post.lastAttemptError}</p>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-white/5">
                    {isPublished ? (
                        (() => {
                            const ids = (post.platformPostId || '').split(',').filter(Boolean);
                            const platformEntry = ids.find(e => e.startsWith(`${viewPlatform}:`));
                            const rawValue = platformEntry ? platformEntry.split(':').slice(1).join(':') : null;
                            if (!rawValue) return null;
                            let href;
                            if (viewPlatform === 'instagram') {
                                // Format: {mediaId}|{permalink} or just {mediaId} (legacy)
                                const permalink = rawValue.includes('|') ? rawValue.split('|').slice(1).join('|') : null;
                                if (!permalink) return null;
                                href = permalink;
                            } else {
                                href = `https://www.facebook.com/${rawValue}`;
                            }
                            const label = viewPlatform === 'instagram' ? 'View on Instagram' : 'View on Facebook';
                            return (
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-white/5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition-colors text-xs font-bold uppercase tracking-widest"
                                >
                                    <FiExternalLink className="h-4 w-4" />
                                    {label}
                                </a>
                            );
                        })()
                    ) : (
                        <button
                            onClick={handleEdit}
                            disabled={loading}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-white/5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition-colors text-xs font-bold uppercase tracking-widest disabled:opacity-50"
                        >
                            <FiEdit2 className="h-4 w-4" />
                            Edit
                        </button>
                    )}

                    {isReady && !isPublished && (
                        <button
                            onClick={handleDeployNow}
                            disabled={loading}
                            className="btn btn-primary flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest disabled:opacity-50"
                        >
                            {loading && actionType === 'deploy' ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiSend className="h-4 w-4" />}
                            Deploy Now
                        </button>
                    )}

                    {isMissingVisual && !isPublished && (
                        <button
                            onClick={handleGenerateVisual}
                            disabled={loading}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-yellow-500 text-warm-dark rounded-lg hover:bg-yellow-400 transition-colors text-xs font-bold uppercase tracking-widest disabled:opacity-50"
                        >
                            {loading && actionType === 'generate' ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiImage className="h-4 w-4" />}
                            Generate Visual
                        </button>
                    )}

                    {isFailed && (
                        <button
                            onClick={handleRetry}
                            disabled={loading}
                            className="btn btn-primary flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest disabled:opacity-50"
                        >
                            {loading && actionType === 'retry' ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiSend className="h-4 w-4" />}
                            Publish Now
                        </button>
                    )}

                    {isScheduled && !isPublished && (
                        <button
                            onClick={handleCancel}
                            disabled={loading}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-red-500/10 text-red-400 hover:text-red-300 rounded-lg hover:bg-red-500/20 transition-colors text-xs font-bold uppercase tracking-widest disabled:opacity-50"
                        >
                            {loading && actionType === 'cancel' ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiTrash2 className="h-4 w-4" />}
                            Cancel
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
}

PostSummaryModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    post: PropTypes.object,
    project: PropTypes.object,
    onUpdate: PropTypes.func
};

export default PostSummaryModal;
